import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { connectState } from '@y42/angular/util';
import { UserColorSchemeService } from '@y42/browser/user-color-scheme';
import { Diagram, ObjectData } from 'gojs';
import { BehaviorSubject, debounceTime, map, of, startWith, switchMap, tap } from 'rxjs';
import { GoJsModelProperties } from '../configs';
import { GoJsOverviewPosition } from '../gojs-overview/gojs-overview';

@UntilDestroy()
@Component({
  selector: 'y42-gojs[diagram]',
  templateUrl: './gojs.component.html',
  styleUrls: ['./gojs.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GojsComponent implements AfterViewInit, OnDestroy {
  constructor(private readonly userColorSchemeService: UserColorSchemeService) {
    this.darkModeListener();
  }

  @Input() props: ObjectData = {};
  @Input() diagram!: Diagram;
  @Input() emptyDiagramMessage = '';
  @Input() showOverview = true;
  @Input() overviewPosition = GoJsOverviewPosition.RightWithSidebar;
  @Input() showContextMenu = false;

  @Input() set dataLoading(val: boolean) {
    this.dataLoading$$.next(val);
  }

  @ViewChild('diagramRef') public diagramRef!: ElementRef;

  private readonly modelChanged$$ = new BehaviorSubject<void | null>(null);
  private readonly dataLoading$$ = new BehaviorSubject<boolean>(false);

  readonly state = connectState(this, {
    dataLoading: this.dataLoading$$,
    diagramHasNodes: this.dataLoading$$.pipe(
      switchMap((diagramLoader) =>
        diagramLoader
          ? of(true)
          : this.modelChanged$$.pipe(
              debounceTime(500),
              map(() => this.diagram?.nodes.count > 0),
            ),
      ),
      startWith(true),
    ),
  });

  ngAfterViewInit() {
    this.diagram.div = this.diagramRef.nativeElement;
    this.addListeners();
  }

  ngOnDestroy() {
    this.removeListeners();
  }

  private addListeners() {
    this.diagram.addModelChangedListener(this.modelChangedListener.bind(this));
  }

  private removeListeners() {
    this.diagram.removeModelChangedListener(this.modelChangedListener.bind(this));
  }

  private modelChangedListener(): void {
    this.modelChanged$$.next();
  }

  private darkModeListener() {
    this.userColorSchemeService.hasDarkMode$
      .pipe(
        tap((hasDarkMode) => {
          this.diagram?.model.setDataProperty(
            this.diagram.model.modelData,
            GoJsModelProperties.HasDarkMode,
            hasDarkMode,
          );
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }
}
